import React, { useContext, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { GlobalContext } from "../../Context/GlobalContext";
import { Toaster } from "react-hot-toast";
import { ShieldCheck, RefreshCcw, BarChart2 } from "lucide-react";
import MainDashboard from "./MainDashboard";
import OrdersDashboard from "./OrdersDashboard";
import OrderChatDrawer from "../common/OrderChatDrawer";

/* Typewriter hook */
function useTypewriter(messages, speed = 24, hold = 1200) {
  const [i, setI] = useState(0);
  const [txt, setTxt] = useState("");
  const [phase, setPhase] = useState("typing");
  useEffect(() => {
    let t;
    if (phase === "typing") {
      if (txt.length < messages[i].length) t = setTimeout(() => setTxt(messages[i].slice(0, txt.length + 1)), speed);
      else setPhase("holding");
    } else if (phase === "holding") {
      t = setTimeout(() => setPhase("deleting"), hold);
    } else if (phase === "deleting") {
      if (txt.length > 0) t = setTimeout(() => setTxt(txt.slice(0, -1)), speed / 1.6);
      else { setI((i + 1) % messages.length); setPhase("typing"); }
    }
    return () => clearTimeout(t);
  }, [messages, i, txt, phase, speed, hold]);
  return txt;
}

export default function AdminDashboard() {
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [ordersRefreshTick, setOrdersRefreshTick] = useState(0);

  const { currentAdmin, refreshDashboardAnalytics, ensureAdmin } = useContext(GlobalContext);

  const openChatDrawer = (orderId) => {
    if (!orderId) return;

    setSelectedOrderId(orderId);
    setChatDrawerOpen(true);
  };
  const [mode, setMode] = useState("main"); // main | orders
  const tips = useRef([
    "Manage campaigns and templates responsibly.",
    "Review settlements and download reports securely.",
    "Track orders in real time; verify anomalies.",
    "Use data filters smartly to reduce noise.",
    "Contact the tech team if anything looks off."
  ]);
  const typed = useTypewriter(tips.current, 24, 1200);
  //   useEffect(() => {
  //   refreshDashboardAnalytics();
  // }, [refreshDashboardAnalytics]);
  useEffect(() => { ensureAdmin(); }, [ensureAdmin]);

  const handleRefresh = () => {
    refreshDashboardAnalytics();
    setOrdersRefreshTick((prev) => prev + 1);
  };
  return (
    <div className="py-1 px-3 lg:py-2px-3">
      <Toaster position="top-right" />

      {/* Welcome box */}
      <div className="mb-4 rounded-2xl border bg-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[#01C37D]/15 text-[#01C37D]">
              <ShieldCheck size={20} />
            </div>
            <div>
              <h1 className="text-xl font-semibold">Welcome, {currentAdmin?.name || "Admin"} 👋</h1>
              <div className="text-xs text-gray-600 h-5">
                <span className="font-medium">{typed}</span>
                <span className="ml-0.5 inline-block w-2 animate-pulse rounded bg-gray-700 align-middle">&nbsp;</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setMode(mode === "main" ? "orders" : "main")}
              className="inline-flex h-9 items-center gap-2 rounded-xl bg-[#01C37D]/15 text-[#01C37D] px-3 text-sm  hover:bg-[#01C37D] hover:text-white"
              title={mode === "main" ? "Open Orders Dashboard" : "Back to Main Dashboard"}
            >
              <BarChart2 size={16} /> {mode === "main" ? "Open Orders Dashboard" : "Back to Main"}
            </button>

            <button
              onClick={handleRefresh}
              className="inline-flex h-9 items-center gap-2 rounded-xl border bg-white px-3 text-sm hover:bg-gray-50"
              title="Refresh analytics"
            >
              <RefreshCcw size={16} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Slide between dashboards */}
      <div className="overflow-hidden">
        <motion.div
          className="flex w-[200%] gap-4"
          animate={{ x: mode === "main" ? "0%" : "-50%" }}
          transition={{ type: "spring", stiffness: 220, damping: 28 }}
        >
          <div className="w-1/2"><MainDashboard /></div>
          <div className="w-1/2">
            <OrdersDashboard
              openChatDrawer={openChatDrawer}
              refreshTick={ordersRefreshTick}
            />
          </div>
        </motion.div>
      </div>
      {chatDrawerOpen && selectedOrderId && (
        <OrderChatDrawer
          open={chatDrawerOpen}
          onClose={() => {
            setChatDrawerOpen(false);
            setSelectedOrderId("");
          }}
          orderId={selectedOrderId}
        />
      )}
    </div>
  );
}


